import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Brain, Database, Activity, Network, Search } from 'lucide-react';

const HomePage = () => {
  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "NLP-Анализ",
      description: "Автоматическая классификация текстов по типам информационных операций"
    },
    {
      icon: <Database className="w-8 h-8" />,
      title: "Военный тезаурус",
      description: "Специализированная база военной терминологии и понятий"
    },
    {
      icon: <Network className="w-8 h-8" />,
      title: "Интеграция соцсетей",
      description: "Мониторинг Telegram, Twitter/X, Facebook для выявления ИО"
    },
    {
      icon: <Activity className="w-8 h-8" />,
      title: "Аналитика",
      description: "Визуализация паттернов и трендов информационных операций"
    },
    {
      icon: <Search className="w-8 h-8" />,
      title: "Поиск и фильтрация",
      description: "Интеллектуальный поиск по терминам и контексту"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Детекция фейков",
      description: "Выявление признаков дезинформации и манипуляций"
    }
  ];

  const ioTypes = [
    { name: "Дезинформация", color: "bg-red-500" },
    { name: "Деморализация", color: "bg-orange-500" },
    { name: "Дискредитация", color: "bg-yellow-500" },
    { name: "Запугивание", color: "bg-purple-500" },
    { name: "Разжигание ненависти", color: "bg-pink-500" },
    { name: "Создание паники", color: "bg-indigo-500" },
    { name: "Провокация", color: "bg-blue-500" },
    { name: "Подрыв доверия", color: "bg-cyan-500" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-blue-50 rounded-full">
            <Shield className="w-5 h-5 text-blue-600" />
            <span className="text-sm font-medium text-blue-600">Система анализа информационных операций</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Интеллектуальная система
            <span className="block text-blue-600 mt-2">выявления ИО</span>
          </h1>
          
          <p className="text-xl text-gray-600 mb-10 leading-relaxed">
            Автоматическое выявление, анализ и классификация информационных операций 
            в социальных сетях и новостных источниках с использованием NLP и машинного обучения
          </p>
          
          <div className="flex gap-4 justify-center flex-wrap">
            <Link 
              to="/analyzer" 
              className="px-8 py-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
              data-testid="analyze-text-btn"
            >
              Анализировать текст
            </Link>
            <Link 
              to="/thesaurus" 
              className="px-8 py-4 bg-white text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors border-2 border-gray-200"
              data-testid="view-thesaurus-btn"
            >
              Военный тезаурус
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
          Основные возможности системы
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              className="p-6 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all"
              data-testid={`feature-card-${index}`}
            >
              <div className="text-blue-600 mb-4">{feature.icon}</div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* IO Types Section */}
      <section className="container mx-auto px-4 py-16 bg-gray-50 rounded-3xl my-16">
        <h2 className="text-3xl font-bold text-center mb-4 text-gray-900">
          Типы информационных операций
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          Система классифицирует тексты по 8 основным типам информационных операций
        </p>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {ioTypes.map((type, index) => (
            <div 
              key={index}
              className="flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200"
              data-testid={`io-type-${index}`}
            >
              <div className={`w-3 h-3 rounded-full ${type.color}`}></div>
              <span className="font-medium text-gray-800">{type.name}</span>
            </div>
          ))}
        </div>
        <div className="text-center mt-8">
          <Link 
            to="/io-types" 
            className="text-blue-600 font-medium hover:text-blue-700 inline-flex items-center gap-2"
            data-testid="view-io-types-btn"
          >
            Подробнее о типах ИО
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </section>

      {/* Architecture Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
          Архитектура системы
        </h2>
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl border border-gray-200 p-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Сбор данных</h3>
                  <p className="text-gray-600">Интеграция с API соцсетей для автоматической загрузки данных</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">NLP-обработка</h3>
                  <p className="text-gray-600">Извлечение сущностей, смыслов и контекстов с помощью LLM</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Классификация</h3>
                  <p className="text-gray-600">Определение типов информационных операций и детекция фейков</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold">4</span>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">Визуализация</h3>
                  <p className="text-gray-600">Аналитические дашборды и графики для анализа паттернов</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-20">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-3xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Готовы начать анализ?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Попробуйте систему прямо сейчас
          </p>
          <Link 
            to="/analyzer" 
            className="inline-block px-8 py-4 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
            data-testid="cta-analyze-btn"
          >
            Начать анализ
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
